import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, errorRedirect, inject, NothingHere, SubTopBar, Title, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { CloudConfigsStore } from './CloudConfigsStore';
import { CloudConfigStatusItem } from './CloudConfigStatusItem';
import { BsChevronDoubleUp } from '@react-icons/all-files/bs/BsChevronDoubleUp';

export interface ICloudConfigStatusParams {
    guid: string;
}

export interface ICloudConfigStatusListProps extends RouteComponentProps<ICloudConfigStatusParams> {}

@autoinject
@observer
export class CloudConfigStatusList extends React.Component<ICloudConfigStatusListProps> {
    @action
    public async componentDidMount() {
        this.loading = true;
        try {
            await this.cloudConfigsStore.loadAsync(this.props.match.params.guid);
            await this.cloudConfigsStore.loadStatusAsync(this.props.match.params.guid);
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div className="pt-3">
                <Title>{this.cloudConfig?.name ?? 'Cloug Config'} Status</Title>

                <SubTopBar>
                    <LinkContainer exact to="/cloud-configs/configs">
                        <a className="btn btn-link">
                            <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Cloud Configs
                        </a>
                    </LinkContainer>
                </SubTopBar>
                <div className="container-fluid">
                    {this.status && (
                        <div className="row display-status-header p-2 justify-content-between mb-2 text-center">
                            <div className="col-3">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Status</strong>
                                        <div>{this.updatingStatus}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Updates In Progress</strong>
                                        <div>
                                            {this.status.updating}
                                            {this.updating && (
                                                <Tooltip message="Updates in progress">
                                                    <BsChevronDoubleUp className="text-running text-pulse ps-2" />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Updated</strong>
                                        <div>
                                            {this.status.updated}/{this.status.total}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-3">
                                <div className="flex-items-center h-100">
                                    <div className="text-center w-100">
                                        <strong>Errors</strong>
                                        <div className="d-flex justify-content-center">
                                            <div className="ps-4 ms-4">{this.status.errored}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-12 px-0">
                            <table className="table table-striped fixed-header w-100 h-100">
                                <thead>
                                    <tr>
                                        <th>Serial Number</th>

                                        <th>Poll Time</th>

                                        <th>State</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.status?.displays.map(d => (
                                        <CloudConfigStatusItem
                                            display={d}
                                            cloudConfigGuid={this.cloudConfig!.cloudConfigGuid}
                                            key={d}
                                        />
                                    ))}
                                </tbody>
                            </table>

                            <NothingHere loading={this.loading} empty={!this.status?.displays.length} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    @computed
    private get cloudConfig() {
        return this.cloudConfigsStore.configs.get(this.props.match.params.guid);
    }

    @computed
    private get updatingStatus() {
        if (this.status) {
            if (this.status.updated == this.status.total) return 'Up to Date';
            if (this.status.updating > 0) return 'Updating';
            if (this.status.errored > 0) return 'Idle with Errors';
        }

        return 'Idle';
    }

    @computed
    private get status() {
        return this.cloudConfigsStore.configStatuses.get(this.props.match.params.guid);
    }

    @computed
    private get updating() {
        return !!this.status && this.status.updating > 0;
    }

    @observable private loading = false;
    @inject
    private readonly cloudConfigsStore!: CloudConfigsStore;
}
